"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { useFlags } from "@stadium/services/launch-darkly";
import { useFeatureFlag } from "@stadium/services/launch-darkly/use-feature-flag";

import { getStoreID, getStoreNumber } from "@selectors/store";

import { multipleGtmFlag } from "@stadium/services/launch-darkly/constants";

type AnalyticsContextType = {
  isLoading: boolean;
  override: boolean;
};

const AnalyticsContext = createContext<AnalyticsContextType>({
  isLoading: false,
  override: true,
});

export const useAnalyticsContext = () => useContext(AnalyticsContext);

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const storeId = useSelector(getStoreID);
  const storeNumber = useSelector(getStoreNumber) || "";
  const { [multipleGtmFlag]: shopNumbersNotToBeOverridden = "" } = useFlags();
  const { isReady } = useFeatureFlag();

  useEffect(() => {
    if (storeId && isReady) {
      setIsLoading(false);
    }
  }, [storeId, isReady]);
  const value = useMemo(
    () => ({
      isLoading,
      override: !(shopNumbersNotToBeOverridden as string).includes(storeNumber.toString()),
    }),
    [isLoading, shopNumbersNotToBeOverridden, storeNumber],
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

export default AnalyticsProvider;
